import {
    type ReactNode,
    useMemo
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ARABIC_READING_FONT_WEIGHTS,
    ARABIC_READING_FONT_FAMILY,
    ARABIC_LINE_HEIGHT_RATIOS,
    CONTROL_STEP_DOWN_SIZES,
    getAndroidFontFamily,
    getLocaleTypography,
    ARABIC_FONT_WEIGHTS,
    ARABIC_FONT_FAMILY,
    ARABIC_FONT_SCALE,
    NCoreUIKitTheme,
    TYPOGRAPHY_GRID,
    MarkdownViewer,
    CONTROL_SIZES,
    Table,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    DocsTocItem
} from "../../components/toc";
import {
    type DocsStyleTokens,
    PageHeader,
    BulletList,
    InlineCode,
    DemoFrame,
    Paragraph,
    CodeBlock,
    SubTitle,
    Callout,
    Article,
    Section,
    Preview,
    Stats,
    Prose,
    Term
} from "../../components/content";
import {
    getFontCss
} from "../../fonts";

type TypographyKeyType = keyof NCoreUIKit.Typography;

type TypeRoleType = "headline" | "display" | "reading" | "title" | "label" | "body" | "mono";

type DensityType = DocsStyleTokens["resolvedGapPropagation"];

type ControlSizeType = keyof typeof CONTROL_SIZES;

type ArabicRatioKeyType = keyof typeof ARABIC_LINE_HEIGHT_RATIOS;

type LoadedFaceType = {
    weight: number;
    style: string;
};

type ArabicPairType = {
    arabicFamily: string;
    latinFamily: string;
    alefHeight: number;
    capHeight: number;
};

type InkRowType = {
    ratioKey: ArabicRatioKeyType;
    textKey: string;
    span: number;
};

const ARABIC_LOCALE = "ar";

const UI_RATIO_FLOOR = 1.25;

const HEADLINE_LEADING_OFFSET = 8;

const DISPLAY_TRACKING = -0.02;

const HEADLINE_TRACKING = -0.01;

const CLOSE_TOLERANCE = 0.005;

const GEIST_X_HEIGHT = 0.53;

const ARABIC_BODY_HEIGHT = 0.491;

const GEIST_ONE_WIDTH = 0.384;

const GEIST_ZERO_WIDTH = 0.663;

const GEIST_TABULAR_WIDTH = 0.6;

const MONO_DIGIT_WIDTH = 0.6;

const SERIF_DIGIT_WIDTH = 0.5;

const VOWELLED_SANS_SPAN = 1.586;

const TRACKING_FOCUS_KEY: TypographyKeyType = "headlineLargeSize";

const ITALIC_STYLE = "italic";

const TERM_PATTERN = /\{\{term:([a-zA-Z0-9]+)\}\}/;

const FONT_FACE_PATTERN = /font-family: "([^"]+)"; font-weight: (\d+); font-style: (\w+);/g;

const READING_KEYS: Array<TypographyKeyType> = [
    "readingSmallSize",
    "readingMediumSize",
    "readingLargeSize"
];

const ROLES: Array<TypeRoleType> = [
    "display",
    "headline",
    "title",
    "body",
    "reading",
    "label",
    "mono"
];

const ROLE_TEXT_KEYS: Record<TypeRoleType, {
    sample: string;
    use: string;
}> = {
    headline: {
        sample: "typographyPage.sampleHeadline",
        use: "typographyPage.roleHeadline"
    },
    display: {
        sample: "typographyPage.sampleDisplay",
        use: "typographyPage.roleDisplay"
    },
    reading: {
        sample: "typographyPage.sampleReading",
        use: "typographyPage.roleReading"
    },
    title: {
        sample: "typographyPage.sampleTitle",
        use: "typographyPage.roleTitle"
    },
    label: {
        sample: "typographyPage.sampleLabel",
        use: "typographyPage.roleLabel"
    },
    body: {
        sample: "typographyPage.sampleBody",
        use: "typographyPage.roleBody"
    },
    mono: {
        sample: "typographyPage.sampleMono",
        use: "typographyPage.roleMono"
    }
};

const DENSITY_KEYS: Record<DensityType, string> = {
    spacious: "typographyPage.densitySpacious",
    compact: "typographyPage.densityCompact"
};

const CONTROL_SIZE_ORDER: Array<ControlSizeType> = [
    "small",
    "medium",
    "large"
];

const DENSITY_TYPE_KEYS: Array<TypographyKeyType> = [
    "titleLargeSize",
    "bodyLargeSize",
    "bodyMediumSize",
    "labelMediumSize"
];

const ARABIC_PAIRS: Array<ArabicPairType> = [
    {
        arabicFamily: ARABIC_FONT_FAMILY,
        latinFamily: "Geist",
        alefHeight: 0.715,
        capHeight: 0.71
    },
    {
        arabicFamily: ARABIC_READING_FONT_FAMILY,
        latinFamily: "Source Serif 4",
        alefHeight: 0.671,
        capHeight: 0.67
    }
];

const INK_ROWS: Array<InkRowType> = [
    {
        textKey: "typographyPage.inkLatin",
        ratioKey: "ui",
        span: 1.131
    },
    {
        textKey: "typographyPage.inkArabicUi",
        ratioKey: "ui",
        span: 1.159
    },
    {
        textKey: "typographyPage.inkArabicVowelled",
        span: VOWELLED_SANS_SPAN,
        ratioKey: "ui"
    },
    {
        textKey: "typographyPage.inkArabicNaskh",
        ratioKey: "reading",
        span: 1.426
    }
];

const NUMERAL_SAMPLES = [
    1111.11,
    8080.8,
    4707.07,
    1919.19
];

const USAGE_CODE = `<Text
    variant="bodyLargeSize"
    color="high"
>
    {children}
</Text>

const {
    typography
} = NCoreUIKitTheme.useContext();

const lineHeight = typography.bodyLargeSize.lineHeight;`;

const READING_CODE = `<MarkdownViewer
    content={article}
    isReading={true}
/>

<View
    style={{
        maxWidth: shapeConfigs.readingMaxWidth
    }}
>
    <Text
        variant="readingMediumSize"
        color="high"
    >
        {paragraph}
    </Text>
</View>`;

const NUMERALS_CODE = `<Text
    style={{
        fontVariant: [
            "tabular-nums"
        ]
    }}
    variant="bodyMediumSize"
    color="high"
>
    {total}
</Text>`;

const getLoadedFaces = () => Array.from(getFontCss().matchAll(FONT_FACE_PATTERN)).reduce<Record<string, Array<LoadedFaceType>>>((faces, match) => {
    const family = match[1] ?? "";

    return {
        ...faces,
        [family]: [
            ...faces[family] ?? [],
            {
                weight: Number(match[2]),
                style: match[3] ?? ""
            }
        ]
    };
}, {});

const LOADED_FACES = getLoadedFaces();

const useTokenTypography = (): NCoreUIKit.Typography => {
    const {
        typography
    } = NCoreUIKitTheme.useContext();

    return useMemo(() => NCoreUIKitTheme.state.typography, [
        typography
    ]);
};

const getVariantKeys = (typography: NCoreUIKit.Typography) => Object.keys(typography) as Array<TypographyKeyType>;

const getRole = (key: string) => ROLES.find((role) => key.startsWith(role));

const getRatio = (token: NCoreUIKit.TypographyToken) => token.lineHeight / token.fontSize;

const isClose = (first: number, second: number) => Math.abs(first - second) < CLOSE_TOLERANCE;

const isOnGrid = (value: number) => value % TYPOGRAPHY_GRID === 0;

const isSameToken = (first: NCoreUIKit.TypographyToken, second: NCoreUIKit.TypographyToken) => first.letterSpacing === second.letterSpacing && first.fontWeight === second.fontWeight && first.fontFamily === second.fontFamily && first.lineHeight === second.lineHeight && first.fontSize === second.fontSize;

const getRoleTokens = (typography: NCoreUIKit.Typography, role: TypeRoleType) => getVariantKeys(typography).filter((key) => getRole(key) === role).map((key) => typography[key]);

const getFloorLineHeight = (fontSize: number) => Math.ceil(fontSize * UI_RATIO_FLOOR / TYPOGRAPHY_GRID) * TYPOGRAPHY_GRID;

const getAscendingSizes = (tokens: Array<NCoreUIKit.TypographyToken>) => [
    ...new Set(tokens.map((token) => token.fontSize))
].sort((first, second) => first - second);

const getUniqueWeights = (tokens: Array<NCoreUIKit.TypographyToken>) => [
    ...new Set(tokens.map((token) => token.fontWeight))
].sort((first, second) => Number(first) - Number(second));

const formatNumber = (language: string, value: number, digits = 2) => new Intl.NumberFormat(language, {
    maximumFractionDigits: digits
}).format(value);

const formatFixed = (language: string, value: number, digits: number) => new Intl.NumberFormat(language, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
}).format(value);

const formatPercent = (language: string, value: number) => new Intl.NumberFormat(language, {
    maximumFractionDigits: 0,
    style: "percent"
}).format(value);

const getMetrics = (token: NCoreUIKit.TypographyToken) => `${token.fontSize}/${token.lineHeight}`;

const getAndroidNames = (fontFamily: string, fontWeights: Array<string | number>) => fontWeights.flatMap((fontWeight) => {
    const upright = getAndroidFontFamily({
        fontFamily,
        fontWeight
    });

    const italic = getAndroidFontFamily({
        fontStyle: ITALIC_STYLE,
        fontFamily,
        fontWeight
    });

    return italic === upright ? [
        upright
    ] : [
        upright,
        italic
    ];
});

const withTerms = (text: string): Array<ReactNode> => text.split(TERM_PATTERN).map((part, index) => index % 2 === 1 ? <Term
    key={`${part}-${index}`}
    id={part}
/> : part);

const stylesheet = StyleSheet.create({
    specimenList: {
        alignItems: "flex-start",
        width: "100%"
    },
    nameStack: {
        alignItems: "flex-start"
    },
    tabularNumber: {
        fontVariant: [
            "tabular-nums"
        ]
    },
    clipList: {
        width: "100%"
    },
    readingPreview: {
        width: "100%"
    },
    numeralPreview: {
        width: "100%"
    }
});

const useTypographyStyles = () => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    return useMemo(() => ({
        clipLine: {
            paddingHorizontal: spaces.spacingMd
        },
        cell: {
            paddingVertical: spaces.spacingSm
        },
        nameStack: {
            gap: spaces.spacingXxs
        },
        clipList: {
            gap: spaces.spacingMd
        },
        specimenItem: {
            gap: spaces.spacingXs
        },
        specimenList: {
            gap: spaces.spacingLg
        }
    }), [
        spaces
    ]);
};

const useFormat = () => {
    const {
        language
    } = useDocsI18n();

    return useMemo(() => ({
        number: (value: number, digits?: number) => formatNumber(language, value, digits),
        fixed: (value: number, digits: number) => formatFixed(language, value, digits),
        percent: (value: number) => formatPercent(language, value)
    }), [
        language
    ]);
};

const TypeTable = ({
    spans = [],
    headers,
    rows
}: {
    rows: Array<Array<ReactNode>>;
    headers: Array<string>;
    spans?: Array<number>;
}) => {
    const styles = useTypographyStyles();

    return <Table
        size="medium"
    >
        <Table.Header>
            <Table.Row>
                {
                    headers.map((header, index) => <Table.Head
                        colSpan={spans[index] ?? 1}
                        key={header}
                    >
                        {header}
                    </Table.Head>)
                }
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {
                rows.map((row, rowIndex) => <Table.Row
                    key={rowIndex}
                >
                    {
                        row.map((cell, cellIndex) => <Table.Cell
                            colSpan={spans[cellIndex] ?? 1}
                            key={cellIndex}
                        >
                            <View
                                style={styles.cell}
                            >
                                {
                                    typeof cell === "string" ?
                                        <Text
                                            variant="bodyMediumSize"
                                            color="high"
                                        >
                                            {cell}
                                        </Text>
                                    :
                                        cell
                                }
                            </View>
                        </Table.Cell>)
                    }
                </Table.Row>)
            }
        </Table.Body>
    </Table>;
};

const Metric = ({
    main,
    sub
}: {
    main: ReactNode;
    sub: string;
}) => {
    const styles = useTypographyStyles();

    return <View
        style={[
            stylesheet.nameStack,
            styles.nameStack
        ]}
    >
        {
            typeof main === "string" ?
                <Text
                    variant="bodyMediumSize"
                    color="high"
                >
                    {main}
                </Text>
            :
                main
        }
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {sub}
        </Text>
    </View>;
};

const Code = ({
    children
}: {
    children: string;
}) => <InlineCode>
    {children}
</InlineCode>;

const NameStack = ({
    names
}: {
    names: Array<string>;
}) => {
    const styles = useTypographyStyles();

    return <View
        style={[
            stylesheet.nameStack,
            styles.nameStack
        ]}
    >
        {
            names.map((name) => <Code
                key={name}
            >
                {name}
            </Code>)
        }
    </View>;
};

const OverviewSection = () => {
    const {
        t
    } = useDocsI18n();

    const {
        resolvedGapPropagation,
        typography
    } = NCoreUIKitTheme.useContext();

    const keys = getVariantKeys(typography);

    const sizes = keys.map((key) => typography[key].fontSize);

    const families = new Set(keys.map((key) => typography[key].fontFamily));

    const roles = new Set(keys.map(getRole));

    return <Section
        title={t("section.overview")}
        id="overview"
    >
        <Prose
            text={t("typographyPage.overviewText", [
                t(DENSITY_KEYS[resolvedGapPropagation])
            ])}
        />
        <Stats
            items={[
                {
                    value: `${Math.min(...sizes)}–${Math.max(...sizes)}`,
                    label: t("typographyPage.statSizes")
                },
                {
                    label: t("typographyPage.statGrid"),
                    value: String(TYPOGRAPHY_GRID)
                },
                {
                    label: t("typographyPage.statVariants"),
                    value: String(keys.length)
                },
                {
                    label: t("typographyPage.statFamilies"),
                    value: String(families.size)
                },
                {
                    label: t("typographyPage.statRoles"),
                    value: String(roles.size)
                }
            ]}
        />
    </Section>;
};

const FamiliesSection = () => {
    const {
        t
    } = useDocsI18n();

    const typography = useTokenTypography();

    const keys = getVariantKeys(typography);

    const families = [
        ...new Set(keys.map((key) => typography[key].fontFamily))
    ];

    const getFaceText = (faces: Array<LoadedFaceType>) => faces.map((face) => face.style === ITALIC_STYLE ? t("typographyPage.faceItalic", [
        face.weight
    ]) : String(face.weight)).join(" · ");

    const getFamilyRoleKey = (familyKeys: Array<TypographyKeyType>) => {
        const familyRoles = new Set(familyKeys.map(getRole));

        if(familyRoles.size === 1 && familyRoles.has("mono")) {
            return "typographyPage.familyRoleCode";
        }

        if(familyRoles.size === 1 && familyRoles.has("reading")) {
            return "typographyPage.familyRoleReading";
        }

        return "typographyPage.familyRoleUi";
    };

    const familyRows = families.map((family) => {
        const familyKeys = keys.filter((key) => typography[key].fontFamily === family);

        const familyTokens = familyKeys.map((key) => typography[key]);

        const usedWeights = getUniqueWeights(familyTokens);

        return [
            <Metric
                sub={t(getFamilyRoleKey(familyKeys))}
                main={<Code>
                    {family}
                </Code>}
                key={family}
            />,
            getFaceText(LOADED_FACES[family] ?? []),
            <Metric
                sub={t("typographyPage.variantCount", [
                    familyTokens.length
                ])}
                main={usedWeights.join(" · ")}
                key={`${family}-used`}
            />,
            <NameStack
                names={getAndroidNames(family, usedWeights)}
                key={`${family}-android`}
            />
        ];
    });

    const readingCount = keys.filter((key) => getRole(key) === "reading").length;

    const arabicCount = keys.filter((key) => getRole(key) !== "mono").length - readingCount;

    const arabicRows = [
        {
            roleKey: "typographyPage.familyRoleArabicUi",
            weights: ARABIC_FONT_WEIGHTS,
            family: ARABIC_FONT_FAMILY,
            count: arabicCount
        },
        {
            roleKey: "typographyPage.familyRoleArabicReading",
            weights: ARABIC_READING_FONT_WEIGHTS,
            family: ARABIC_READING_FONT_FAMILY,
            count: readingCount
        }
    ].map(({
        roleKey,
        weights,
        family,
        count
    }) => [
        <Metric
            sub={t(roleKey)}
            main={<Code>
                {family}
            </Code>}
            key={family}
        />,
        weights.join(" · "),
        <Metric
            sub={t("typographyPage.familyArabicCount", [
                count
            ])}
            main={weights.join(" · ")}
            key={`${family}-used`}
        />,
        <NameStack
            names={getAndroidNames(family, weights)}
            key={`${family}-android`}
        />
    ]);

    return <Section
        title={t("typographyPage.familiesTitle")}
        id="families"
    >
        <Prose
            text={t("typographyPage.familiesText")}
        />
        <BulletList
            items={[
                withTerms(t("typographyPage.familiesGeist")),
                withTerms(t("typographyPage.familiesMono")),
                withTerms(t("typographyPage.familiesSerif"))
            ]}
        />
        <Prose
            text={t("typographyPage.familiesLicense")}
        />
        <TypeTable
            headers={[
                t("typographyPage.colFamily"),
                t("typographyPage.colPackaged"),
                t("typographyPage.colUsedWeights"),
                t("typographyPage.colAndroid")
            ]}
            rows={[
                ...familyRows,
                ...arabicRows
            ]}
            spans={[
                1,
                1,
                1,
                2
            ]}
        />
        <SubTitle>
            {t("typographyPage.loadingTitle")}
        </SubTitle>
        <BulletList
            items={[
                withTerms(t("typographyPage.loadingWeb")),
                withTerms(t("typographyPage.loadingNative")),
                withTerms(t("typographyPage.loadingAndroid"))
            ]}
        />
    </Section>;
};

const RolesSection = () => {
    const {
        t
    } = useDocsI18n();

    const {
        controlHeights,
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    const typography = useTokenTypography();

    const format = useFormat();

    const keys = getVariantKeys(typography);

    const roleRows = ROLES.map((role) => {
        const roleKeys = keys.filter((key) => getRole(key) === role);

        const roleTokens = roleKeys.map((key) => typography[key]);

        return [
            <Metric
                sub={[
                    ...new Set(roleTokens.map((token) => token.fontFamily))
                ].join(" · ")}
                main={<Code>
                    {role}
                </Code>}
                key={role}
            />,
            <NameStack
                names={roleKeys.map((key) => `${key} · ${getMetrics(typography[key])}`)}
                key={`${role}-variants`}
            />,
            getUniqueWeights(roleTokens).join(" · "),
            t(ROLE_TEXT_KEYS[role].use)
        ];
    });

    const uiSizes = getAscendingSizes(keys.filter((key) => getRole(key) !== "reading").map((key) => typography[key]));

    const readingSizes = getAscendingSizes(READING_KEYS.map((key) => typography[key]));

    const sizeSteps = uiSizes.slice(1).map((size, index) => size - (uiSizes[index] ?? size));

    const sizeRatios = uiSizes.slice(1).map((size, index) => size / (uiSizes[index] ?? size));

    const controlRows = CONTROL_SIZE_ORDER.map((size) => {
        const {
            labelVariant,
            valueVariant,
            height
        } = CONTROL_SIZES[size];

        const stepDownLabel = CONTROL_STEP_DOWN_SIZES[size].labelVariant;

        return [
            <Metric
                sub={t("typographyPage.controlHeightValue", [
                    controlHeights[height]
                ])}
                main={<Code>
                    {size}
                </Code>}
                key={size}
            />,
            <NameStack
                names={[
                    labelVariant,
                    getMetrics(typography[labelVariant])
                ]}
                key={`${size}-label`}
            />,
            <NameStack
                names={[
                    valueVariant,
                    getMetrics(typography[valueVariant])
                ]}
                key={`${size}-value`}
            />,
            <NameStack
                names={[
                    stepDownLabel,
                    getMetrics(typography[stepDownLabel])
                ]}
                key={`${size}-step-down`}
            />
        ];
    });

    return <Section
        title={t("typographyPage.rolesTitle")}
        id="roles"
    >
        <Prose
            text={t("typographyPage.rolesText")}
        />
        <TypeTable
            headers={[
                t("typographyPage.colRole"),
                t("typographyPage.colVariants"),
                t("typographyPage.colWeight"),
                t("typographyPage.colUse")
            ]}
            rows={roleRows}
            spans={[
                1,
                2,
                1,
                2
            ]}
        />
        <Prose
            text={t("typographyPage.scaleText", [
                uiSizes.join(" · "),
                sizeSteps.join(", "),
                format.percent(Math.min(...sizeRatios) - 1),
                format.percent(Math.max(...sizeRatios) - 1),
                readingSizes.join(" · ")
            ])}
        />
        <SubTitle>
            {t("typographyPage.controlsTitle")}
        </SubTitle>
        <Prose
            text={t("typographyPage.controlsText", [
                shapeConfigs.chipHeightOffset
            ])}
        />
        <TypeTable
            headers={[
                t("typographyPage.colControlSize"),
                t("typographyPage.colLabel"),
                t("typographyPage.colValue"),
                t("typographyPage.colStepDown")
            ]}
            rows={controlRows}
            spans={[
                1,
                2,
                2,
                2
            ]}
        />
    </Section>;
};

const SpecimenSection = () => {
    const {
        t
    } = useDocsI18n();

    const {
        resolvedGapPropagation,
        typography
    } = NCoreUIKitTheme.useContext();

    const styles = useTypographyStyles();

    const keys = getVariantKeys(typography);

    const getSample = (key: TypographyKeyType) => {
        const role = getRole(key);

        if(role === "mono") {
            return t(ROLE_TEXT_KEYS.mono.sample, [
                typography.bodyLargeSize.fontSize,
                typography.bodyLargeSize.lineHeight
            ]);
        }

        return t(ROLE_TEXT_KEYS[role ?? "body"].sample);
    };

    return <Section
        title={t("typographyPage.specimenTitle")}
        id="specimen"
    >
        <Paragraph>
            {t("typographyPage.specimenText")}
        </Paragraph>
        <Preview
            caption={t("typographyPage.specimenCaption", [
                keys.length,
                t(DENSITY_KEYS[resolvedGapPropagation])
            ])}
        >
            <View
                style={[
                    stylesheet.specimenList,
                    styles.specimenList
                ]}
            >
                {
                    keys.map((key) => <View
                        style={styles.specimenItem}
                        key={key}
                    >
                        <Text
                            variant="bodySmallSize"
                            color="low"
                        >
                            {`${key} · ${getMetrics(typography[key])} · ${typography[key].fontWeight}`}
                        </Text>
                        <Text
                            variant={key}
                            color="high"
                        >
                            {getSample(key)}
                        </Text>
                    </View>)
                }
            </View>
        </Preview>
    </Section>;
};

const LineHeightSection = () => {
    const {
        t
    } = useDocsI18n();

    const {
        controlHeights,
        typography
    } = NCoreUIKitTheme.useContext();

    const styles = useTypographyStyles();

    const format = useFormat();

    const keys = getVariantKeys(typography);

    const uiKeys = keys.filter((key) => getRole(key) !== "reading");

    const headlineTokens = getRoleTokens(typography, "headline");

    const headlineChain = [
        ...headlineTokens
    ].sort((first, second) => first.fontSize - second.fontSize).map(getMetrics).join(" · ");

    const patternKeys = uiKeys.filter((key) => typography[key].lineHeight === getFloorLineHeight(typography[key].fontSize));

    const patternExceptions = uiKeys.filter((key) => !patternKeys.includes(key));

    const uiRatios = uiKeys.map((key) => getRatio(typography[key]));

    const minRatio = Math.min(...uiRatios);

    const minKeys = uiKeys.filter((key) => isClose(getRatio(typography[key]), minRatio));

    const readingRatios = READING_KEYS.map((key) => getRatio(typography[key]));

    const gridCount = keys.filter((key) => isOnGrid(typography[key].lineHeight)).length;

    const floorCount = uiKeys.filter((key) => getRatio(typography[key]) >= UI_RATIO_FLOOR).length;

    const numericCount = keys.filter((key) => typeof typography[key].lineHeight === "number").length;

    const headlineCount = headlineTokens.filter((token) => token.lineHeight - token.fontSize === HEADLINE_LEADING_OFFSET).length;

    const body = typography.bodyLargeSize;

    const controlHeightList = CONTROL_SIZE_ORDER.map((size) => controlHeights[CONTROL_SIZES[size].height]).join(", ");

    return <Section
        title={t("typographyPage.lineHeightTitle")}
        id="line-height"
    >
        <Prose
            text={t("typographyPage.lineHeightText")}
        />
        <BulletList
            items={[
                withTerms(t("typographyPage.lineHeightGrid", [
                    TYPOGRAPHY_GRID,
                    controlHeightList
                ])),
                withTerms(t("typographyPage.lineHeightFloor", [
                    format.number(UI_RATIO_FLOOR)
                ])),
                withTerms(t("typographyPage.lineHeightPattern", [
                    patternKeys.length,
                    uiKeys.length,
                    format.number(UI_RATIO_FLOOR),
                    TYPOGRAPHY_GRID,
                    patternExceptions.join(", ") || "—"
                ])),
                withTerms(t("typographyPage.lineHeightBody", [
                    body.fontSize,
                    body.lineHeight,
                    format.number(getRatio(body))
                ])),
                withTerms(t("typographyPage.lineHeightHeadline", [
                    HEADLINE_LEADING_OFFSET,
                    headlineChain
                ])),
                withTerms(t("typographyPage.lineHeightReading", [
                    `${format.number(Math.min(...readingRatios))}–${format.number(Math.max(...readingRatios))}`,
                    READING_KEYS.map((key) => `${key} ${getMetrics(typography[key])}`).join(" · ")
                ]))
            ]}
        />
        <SubTitle>
            {t("typographyPage.checkTitle")}
        </SubTitle>
        <Paragraph>
            {t("typographyPage.checkText")}
        </Paragraph>
        <TypeTable
            rows={[
                [
                    t("typographyPage.checkGrid", [
                        TYPOGRAPHY_GRID
                    ]),
                    `${gridCount}/${keys.length}`
                ],
                [
                    t("typographyPage.checkFloor", [
                        format.number(UI_RATIO_FLOOR)
                    ]),
                    t("typographyPage.checkFloorResult", [
                        `${floorCount}/${uiKeys.length}`,
                        format.number(minRatio),
                        minKeys.join(", ")
                    ])
                ],
                [
                    t("typographyPage.checkHeadline", [
                        HEADLINE_LEADING_OFFSET
                    ]),
                    `${headlineCount}/${headlineTokens.length}`
                ],
                [
                    t("typographyPage.checkNumeric"),
                    `${numericCount}/${keys.length}`
                ]
            ]}
            headers={[
                t("typographyPage.colRule"),
                t("typographyPage.colResult")
            ]}
            spans={[
                3,
                2
            ]}
        />
        <SubTitle>
            {t("typographyPage.clipTitle")}
        </SubTitle>
        <Prose
            text={t("typographyPage.clipText")}
        />
        <Preview
            caption={t("typographyPage.clipCaption")}
        >
            <View
                style={[
                    stylesheet.clipList,
                    styles.clipList
                ]}
            >
                {
                    keys.map((key) => <View
                        style={styles.specimenItem}
                        key={key}
                    >
                        <Text
                            variant="bodySmallSize"
                            color="low"
                        >
                            {`${key} · ${typography[key].lineHeight}`}
                        </Text>
                        <DemoFrame
                            height={typography[key].lineHeight}
                        >
                            <View
                                style={styles.clipLine}
                            >
                                <Text
                                    numberOfLines={1}
                                    variant={key}
                                    color="high"
                                >
                                    {t("typographyPage.clipSample")}
                                </Text>
                            </View>
                        </DemoFrame>
                    </View>)
                }
            </View>
        </Preview>
    </Section>;
};

const ReadingSection = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        typography,
        spaces
    } = NCoreUIKitTheme.useContext();

    const format = useFormat();

    const arabicTypography = getLocaleTypography({
        locale: ARABIC_LOCALE,
        typography
    });

    const readingRatios = READING_KEYS.map((key) => getRatio(typography[key]));

    const readingContent = [
        `### ${t("typographyPage.readingSampleHeading")}`,
        "",
        t("typographyPage.readingSampleParagraph1"),
        "",
        t("typographyPage.readingSampleParagraph2"),
        "",
        `- ${t("typographyPage.readingSampleItem1")}`,
        `- ${t("typographyPage.readingSampleItem2")}`
    ].join("\n");

    const rows: Array<Array<ReactNode>> = [
        [
            t("typographyPage.readingTopicSize"),
            t("typographyPage.readingRuleSize"),
            <NameStack
                names={READING_KEYS.map((key) => `${key} ${getMetrics(typography[key])}`)}
                key="size"
            />
        ],
        [
            t("typographyPage.readingTopicLineHeight"),
            t("typographyPage.readingRuleLineHeight", [
                TYPOGRAPHY_GRID
            ]),
            `${format.number(Math.min(...readingRatios))}–${format.number(Math.max(...readingRatios))}`
        ],
        [
            t("typographyPage.readingTopicMeasure"),
            t("typographyPage.readingRuleMeasure"),
            <Metric
                main={`${shapeConfigs.readingMaxWidth} px`}
                sub="readingMaxWidth"
                key="measure"
            />
        ],
        [
            t("typographyPage.readingTopicParagraph"),
            t("typographyPage.readingRuleParagraph"),
            <Metric
                main={`${spaces.spacingLg} px`}
                sub="spacingLg"
                key="paragraph"
            />
        ],
        [
            t("typographyPage.readingTopicHeadings"),
            t("typographyPage.readingRuleHeadings"),
            typography.titleLargeSize.fontFamily
        ],
        [
            t("typographyPage.readingTopicLinks"),
            t("typographyPage.readingRuleLinks"),
            t("typographyPage.readingValueLinks")
        ],
        [
            t("typographyPage.readingTopicEmphasis"),
            t("typographyPage.readingRuleEmphasis"),
            t("typographyPage.readingValueEmphasis")
        ],
        [
            t("typographyPage.readingTopicArabic"),
            t("typographyPage.readingRuleArabic", [
                ARABIC_READING_FONT_FAMILY,
                format.number(ARABIC_FONT_SCALE, 3),
                format.number(ARABIC_LINE_HEIGHT_RATIOS.reading)
            ]),
            <Metric
                main={getMetrics(arabicTypography.readingMediumSize)}
                sub={ARABIC_READING_FONT_FAMILY}
                key="arabic"
            />
        ],
        [
            t("typographyPage.readingTopicCode"),
            t("typographyPage.readingRuleCode"),
            typography.monoMediumSize.fontFamily
        ]
    ];

    return <Section
        title={t("typographyPage.readingTitle")}
        id="reading"
    >
        <Prose
            text={t("typographyPage.readingText")}
        />
        <TypeTable
            headers={[
                t("typographyPage.colTopic"),
                t("typographyPage.colRule"),
                t("typographyPage.colValue")
            ]}
            rows={rows}
            spans={[
                1,
                3,
                1
            ]}
        />
        <Preview
            caption={t("typographyPage.readingCaption")}
        >
            <View
                style={stylesheet.readingPreview}
            >
                <MarkdownViewer
                    content={readingContent}
                    isReading={true}
                />
            </View>
        </Preview>
        <Paragraph>
            {t("typographyPage.readingUsage")}
        </Paragraph>
        <CodeBlock
            code={READING_CODE}
            language="tsx"
        />
    </Section>;
};

const NumeralsSection = () => {
    const {
        t
    } = useDocsI18n();

    const format = useFormat();

    const rows = NUMERAL_SAMPLES.map((value) => [
        <Text
            key={`${value}-proportional`}
            variant="bodyMediumSize"
            color="high"
        >
            {format.fixed(value, 2)}
        </Text>,
        <Text
            style={stylesheet.tabularNumber}
            key={`${value}-tabular`}
            variant="bodyMediumSize"
            color="high"
        >
            {format.fixed(value, 2)}
        </Text>
    ]);

    return <Section
        title={t("typographyPage.numeralsTitle")}
        id="numerals"
    >
        <Prose
            text={t("typographyPage.numeralsText", [
                format.number(GEIST_ONE_WIDTH, 3),
                format.number(GEIST_ZERO_WIDTH, 3),
                format.number(GEIST_TABULAR_WIDTH, 1)
            ])}
        />
        <Prose
            text={t("typographyPage.numeralsKit")}
        />
        <Preview
            caption={t("typographyPage.numeralsCaption")}
        >
            <View
                style={stylesheet.numeralPreview}
            >
                <TypeTable
                    headers={[
                        t("typographyPage.colProportional"),
                        t("typographyPage.colTabular")
                    ]}
                    rows={rows}
                />
            </View>
        </Preview>
        <Paragraph>
            {t("typographyPage.numeralsUsage")}
        </Paragraph>
        <CodeBlock
            code={NUMERALS_CODE}
            language="tsx"
        />
        <Prose
            text={t("typographyPage.numeralsOther", [
                format.number(MONO_DIGIT_WIDTH, 1),
                format.number(SERIF_DIGIT_WIDTH, 1)
            ])}
        />
    </Section>;
};

const TrackingSection = () => {
    const {
        t
    } = useDocsI18n();

    const {
        typography
    } = NCoreUIKitTheme.useContext();

    const format = useFormat();

    const keys = getVariantKeys(typography);

    const trackedKeys = keys.filter((key) => typography[key].letterSpacing !== 0);

    const zeroCount = keys.length - trackedKeys.length;

    const focusToken = typography[TRACKING_FOCUS_KEY];

    const isFormulaMatch = (key: TypographyKeyType, coefficient: number) => isClose(coefficient * typography[key].fontSize, typography[key].letterSpacing);

    const displayKeys = keys.filter((key) => getRole(key) === "display");

    const headlineRestKeys = keys.filter((key) => getRole(key) === "headline" && key !== TRACKING_FOCUS_KEY);

    const isDisplayFormula = displayKeys.every((key) => isFormulaMatch(key, DISPLAY_TRACKING));

    const isHeadlineFormula = headlineRestKeys.every((key) => isFormulaMatch(key, HEADLINE_TRACKING));

    const isFocusOff = !isFormulaMatch(TRACKING_FOCUS_KEY, HEADLINE_TRACKING);

    const getFormula = (key: TypographyKeyType) => {
        const role = getRole(key);

        if(role === "display") {
            return DISPLAY_TRACKING;
        }

        if(role === "headline" && key !== TRACKING_FOCUS_KEY) {
            return HEADLINE_TRACKING;
        }

        return undefined;
    };

    const trackedRows = trackedKeys.map((key) => {
        const token = typography[key];

        const formula = getFormula(key);

        return [
            <Code
                key={key}
            >
                {key}
            </Code>,
            <Metric
                sub={t("typographyPage.emValue", [
                    format.number(token.letterSpacing / token.fontSize, 3)
                ])}
                main={format.number(token.letterSpacing)}
                key={`${key}-tracking`}
            />,
            formula === undefined ? "—" : `${format.number(formula)} × ${token.fontSize} = ${format.number(formula * token.fontSize)}`
        ];
    });

    return <Section
        title={t("typographyPage.trackingTitle")}
        id="letter-spacing"
    >
        <Prose
            text={t("typographyPage.trackingText")}
        />
        <BulletList
            items={[
                withTerms(t(isDisplayFormula ? "typographyPage.trackingDisplay" : "typographyPage.trackingDisplayOff", [
                    format.number(DISPLAY_TRACKING)
                ])),
                withTerms(t(isHeadlineFormula ? "typographyPage.trackingHeadline" : "typographyPage.trackingHeadlineOff", [
                    format.number(HEADLINE_TRACKING),
                    headlineRestKeys.join(", ")
                ])),
                ...isFocusOff ? [
                    withTerms(t("typographyPage.trackingHeadlineLarge", [
                        format.number(focusToken.letterSpacing),
                        format.number(focusToken.letterSpacing / focusToken.fontSize, 3)
                    ]))
                ] : [],
                withTerms(t("typographyPage.trackingRest")),
                withTerms(t("typographyPage.trackingArabic"))
            ]}
        />
        <TypeTable
            rows={[
                ...trackedRows,
                [
                    t("typographyPage.trackingOthers", [
                        zeroCount
                    ]),
                    "0",
                    "—"
                ]
            ]}
            headers={[
                t("typographyPage.colVariant"),
                t("typographyPage.colTracking"),
                t("typographyPage.colFormula")
            ]}
            spans={[
                2,
                1,
                2
            ]}
        />
    </Section>;
};

const WeightsSection = () => {
    const {
        t
    } = useDocsI18n();

    const {
        typography
    } = NCoreUIKitTheme.useContext();

    const keys = getVariantKeys(typography);

    const weights = getUniqueWeights(keys.map((key) => typography[key]));

    const rows = weights.map((fontWeight) => {
        const weightKeys = keys.filter((key) => typography[key].fontWeight === fontWeight);

        const roles = [
            ...new Set(weightKeys.map(getRole))
        ];

        const faceName = getAndroidFontFamily({
            fontFamily: typography[weightKeys[0] ?? "bodyMediumSize"].fontFamily,
            fontWeight
        }).split("-")[1] ?? fontWeight;

        return [
            <Code
                key={fontWeight}
            >
                {fontWeight}
            </Code>,
            faceName,
            <NameStack
                names={roles.map((role) => role ?? "")}
                key={`${fontWeight}-roles`}
            />,
            String(weightKeys.length)
        ];
    });

    return <Section
        title={t("typographyPage.weightsTitle")}
        id="weights"
    >
        <Prose
            text={t("typographyPage.weightsText", [
                weights.length
            ])}
        />
        <TypeTable
            headers={[
                t("typographyPage.colWeight"),
                t("typographyPage.colFace"),
                t("typographyPage.colRoles"),
                t("typographyPage.colVariantCount")
            ]}
            rows={rows}
        />
        <BulletList
            items={[
                withTerms(t("typographyPage.weightsDisplay")),
                withTerms(t("typographyPage.weightsLabel")),
                withTerms(t("typographyPage.weightsHeading")),
                withTerms(t("typographyPage.weightsBody"))
            ]}
        />
        <Paragraph>
            {t("typographyPage.weightsPackage")}
        </Paragraph>
    </Section>;
};

const DensitySection = () => {
    const {
        t
    } = useDocsI18n();

    const spaciousTheme = NCoreUIKitTheme.useContext({
        gapPropagation: "spacious"
    });

    const compactTheme = NCoreUIKitTheme.useContext({
        gapPropagation: "compact"
    });

    const keys = getVariantKeys(spaciousTheme.typography);

    const isCompactLive = compactTheme.resolvedGapPropagation === "compact";

    const sameCount = keys.filter((key) => isSameToken(spaciousTheme.typography[key], compactTheme.typography[key])).length;

    const typeRows = DENSITY_TYPE_KEYS.map((key) => [
        <Code
            key={key}
        >
            {key}
        </Code>,
        getMetrics(spaciousTheme.typography[key]),
        getMetrics(compactTheme.typography[key])
    ]);

    const shapeRows = [
        [
            "spaces.spacingSm",
            spaciousTheme.spaces.spacingSm,
            compactTheme.spaces.spacingSm
        ],
        [
            "spaces.spacingMd",
            spaciousTheme.spaces.spacingMd,
            compactTheme.spaces.spacingMd
        ],
        [
            "controlHeights.small",
            spaciousTheme.controlHeights.small,
            compactTheme.controlHeights.small
        ],
        [
            "controlHeights.medium",
            spaciousTheme.controlHeights.medium,
            compactTheme.controlHeights.medium
        ],
        [
            "controlPaddings.medium",
            spaciousTheme.controlPaddings.medium,
            compactTheme.controlPaddings.medium
        ]
    ].map(([
        name,
        spacious,
        compact
    ]) => [
        <Code
            key={String(name)}
        >
            {String(name)}
        </Code>,
        String(spacious),
        String(compact)
    ]);

    return <Section
        title={t("typographyPage.densityTitle")}
        id="density"
    >
        <Prose
            text={t("typographyPage.densityText")}
        />
        <TypeTable
            headers={[
                t("typographyPage.colToken"),
                t("typographyPage.densitySpacious"),
                t(isCompactLive ? "typographyPage.densityCompact" : "typographyPage.densityCompactClamped")
            ]}
            rows={[
                ...typeRows,
                ...shapeRows
            ]}
            spans={[
                2,
                1,
                1
            ]}
        />
        {
            isCompactLive ?
                <Paragraph>
                    {t("typographyPage.densityLive", [
                        sameCount,
                        keys.length
                    ])}
                </Paragraph>
            :
                <Callout>
                    {t("typographyPage.densityClamped")}
                </Callout>
        }
        <Prose
            text={t("typographyPage.densityPointer", [
                t(DENSITY_KEYS[compactTheme.resolvedGapPropagation])
            ])}
        />
    </Section>;
};

const ArabicSection = () => {
    const {
        t
    } = useDocsI18n();

    const {
        typography
    } = NCoreUIKitTheme.useContext();

    const format = useFormat();

    const keys = getVariantKeys(typography);

    const arabicTypography = getLocaleTypography({
        locale: ARABIC_LOCALE,
        typography
    });

    const scaledSizes = getAscendingSizes(keys.filter((key) => getRole(key) !== "mono").map((key) => typography[key])).map((size) => `${size} → ${Math.round(size * ARABIC_FONT_SCALE)}`).join(", ");

    const scaledBody = ARABIC_BODY_HEIGHT * ARABIC_FONT_SCALE;

    const getArabicFamily = (key: TypographyKeyType) => {
        const role = getRole(key);

        if(role === "mono") {
            return t("typographyPage.scriptRoleNone");
        }

        return role === "reading" ? ARABIC_READING_FONT_FAMILY : ARABIC_FONT_FAMILY;
    };

    const pairRows = ARABIC_PAIRS.map((pair) => [
        `${pair.latinFamily} ↔ ${pair.arabicFamily}`,
        t("typographyPage.emValue", [
            format.fixed(pair.capHeight, 3)
        ]),
        t("typographyPage.emValue", [
            format.fixed(pair.alefHeight, 3)
        ]),
        format.fixed(pair.capHeight / pair.alefHeight, 3)
    ]);

    const inkRows = INK_ROWS.map((row) => {
        const ratio = ARABIC_LINE_HEIGHT_RATIOS[row.ratioKey];

        return [
            t(row.textKey),
            t("typographyPage.emValue", [
                format.fixed(row.span, 3)
            ]),
            format.number(ratio),
            format.percent(row.span / ratio)
        ];
    });

    const variantRows = keys.map((key) => [
        <Code
            key={key}
        >
            {key}
        </Code>,
        <Metric
            sub={t("typographyPage.ratioValue", [
                format.number(getRatio(typography[key]))
            ])}
            main={getMetrics(typography[key])}
            key={`${key}-latin`}
        />,
        <Metric
            sub={t("typographyPage.ratioValue", [
                format.number(getRatio(arabicTypography[key]))
            ])}
            main={getMetrics(arabicTypography[key])}
            key={`${key}-arabic`}
        />,
        getArabicFamily(key)
    ]);

    return <Section
        title={t("typographyPage.arabicTitle")}
        id="arabic"
    >
        <Prose
            text={t("typographyPage.arabicText", [
                ARABIC_FONT_FAMILY,
                ARABIC_READING_FONT_FAMILY
            ])}
        />
        <SubTitle>
            {t("typographyPage.arabicPairTitle")}
        </SubTitle>
        <Prose
            text={t("typographyPage.arabicPairText")}
        />
        <TypeTable
            headers={[
                t("typographyPage.colPair"),
                t("typographyPage.colCapHeight"),
                t("typographyPage.colAlefHeight"),
                t("typographyPage.colRatio")
            ]}
            rows={pairRows}
            spans={[
                2,
                1,
                1,
                1
            ]}
        />
        <Prose
            text={t("typographyPage.arabicStyleText")}
        />
        <SubTitle>
            {t("typographyPage.arabicSizeTitle", [
                format.number(ARABIC_FONT_SCALE, 3)
            ])}
        </SubTitle>
        <Prose
            text={t("typographyPage.arabicSizeText", [
                format.number(GEIST_X_HEIGHT / ARABIC_BODY_HEIGHT),
                format.number(ARABIC_FONT_SCALE, 3),
                format.percent(scaledBody / GEIST_X_HEIGHT - 1),
                format.fixed(scaledBody, 3)
            ])}
        />
        <Prose
            text={t("typographyPage.arabicSizeRound", [
                scaledSizes
            ])}
        />
        <SubTitle>
            {t("typographyPage.arabicLineTitle", [
                format.number(ARABIC_LINE_HEIGHT_RATIOS.ui),
                format.number(ARABIC_LINE_HEIGHT_RATIOS.reading)
            ])}
        </SubTitle>
        <Prose
            text={t("typographyPage.arabicLineText", [
                format.number(ARABIC_LINE_HEIGHT_RATIOS.ui),
                format.number(ARABIC_LINE_HEIGHT_RATIOS.reading),
                TYPOGRAPHY_GRID
            ])}
        />
        <TypeTable
            headers={[
                t("typographyPage.colText"),
                t("typographyPage.colInk"),
                t("typographyPage.colLine"),
                t("typographyPage.colShare")
            ]}
            rows={inkRows}
            spans={[
                3,
                1,
                1,
                1
            ]}
        />
        <Prose
            text={t("typographyPage.arabicLineReason", [
                format.number(ARABIC_LINE_HEIGHT_RATIOS.ui),
                format.number(ARABIC_LINE_HEIGHT_RATIOS.reading),
                format.percent(VOWELLED_SANS_SPAN / ARABIC_LINE_HEIGHT_RATIOS.reading)
            ])}
        />
        <SubTitle>
            {t("typographyPage.arabicWeightTitle")}
        </SubTitle>
        <Prose
            text={t("typographyPage.arabicWeightText", [
                ARABIC_FONT_WEIGHTS.join(" · "),
                ARABIC_READING_FONT_WEIGHTS.join(" · ")
            ])}
        />
        <Prose
            text={t("typographyPage.arabicTrackingText")}
        />
        <SubTitle>
            {t("typographyPage.arabicTableTitle")}
        </SubTitle>
        <TypeTable
            headers={[
                t("typographyPage.colVariant"),
                t("typographyPage.colLatin"),
                t("typographyPage.colArabic"),
                t("typographyPage.colArabicFamily")
            ]}
            rows={variantRows}
            spans={[
                2,
                1,
                1,
                1
            ]}
        />
        <Callout>
            {t("typographyPage.arabicDocsNote")}
        </Callout>
    </Section>;
};

const CoverageSection = () => {
    const {
        t
    } = useDocsI18n();

    return <Section
        title={t("typographyPage.coverageTitle")}
        id="coverage"
    >
        <Prose
            text={t("typographyPage.coverageGeist")}
        />
        <Prose
            text={t("typographyPage.coverageSerif")}
        />
        <Prose
            text={t("typographyPage.coverageOther")}
        />
    </Section>;
};

const UsageSection = () => {
    const {
        t
    } = useDocsI18n();

    return <Section
        title={t("typographyPage.usageTitle")}
        id="usage"
    >
        <Prose
            text={t("typographyPage.usageText")}
        />
        <CodeBlock
            code={USAGE_CODE}
            language="tsx"
        />
        <Prose
            text={t("typographyPage.usageHook")}
        />
        <Paragraph>
            {t("typographyPage.docsMapping")}
        </Paragraph>
    </Section>;
};

export const getTypographyToc = (t: DocsTranslate): Array<DocsTocItem> => [
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("typographyPage.familiesTitle"),
        id: "families"
    },
    {
        title: t("typographyPage.rolesTitle"),
        id: "roles"
    },
    {
        title: t("typographyPage.specimenTitle"),
        id: "specimen"
    },
    {
        title: t("typographyPage.lineHeightTitle"),
        id: "line-height"
    },
    {
        title: t("typographyPage.readingTitle"),
        id: "reading"
    },
    {
        title: t("typographyPage.numeralsTitle"),
        id: "numerals"
    },
    {
        title: t("typographyPage.trackingTitle"),
        id: "letter-spacing"
    },
    {
        title: t("typographyPage.weightsTitle"),
        id: "weights"
    },
    {
        title: t("typographyPage.densityTitle"),
        id: "density"
    },
    {
        title: t("typographyPage.arabicTitle"),
        id: "arabic"
    },
    {
        title: t("typographyPage.coverageTitle"),
        id: "coverage"
    },
    {
        title: t("typographyPage.usageTitle"),
        id: "usage"
    }
];

const TypographyPage = () => {
    const {
        t
    } = useDocsI18n();

    return <Article>
        <PageHeader
            breadcrumbs={[
                {
                    label: t("breadcrumb.docs"),
                    href: "/"
                },
                {
                    label: t("nav.sectionFoundations")
                }
            ]}
            lead={t("foundation.typographyLead")}
            title={t("page.typography")}
        />
        <OverviewSection/>
        <FamiliesSection/>
        <RolesSection/>
        <SpecimenSection/>
        <LineHeightSection/>
        <ReadingSection/>
        <NumeralsSection/>
        <TrackingSection/>
        <WeightsSection/>
        <DensitySection/>
        <ArabicSection/>
        <CoverageSection/>
        <UsageSection/>
    </Article>;
};
export default TypographyPage;
